import { Fragment, useEffect, useState } from 'react';
import StateDots from './StateDots';
import {
  getQuerySetStats, getHiddenSets, setQuerySetHidden, getSetTrends,
  type QuerySetStat, type HiddenSet, type SetTrend,
} from '../account';
import SetTrendChart from './SetTrendChart';
import SetAnalysisPanel from './SetAnalysis';
import { Card, SectionLabel } from './ui';
import Info from './Glossary';

/**
 * Question sets as their own subject, not a column on the run log.
 *
 * The set is the unit that makes a trend readable — two runs asking different
 * questions are two measurements, not two points on a line — so "which of my
 * sets is doing well" is a different question from "what happened on Tuesday",
 * and it was not answerable anywhere.
 */

const fmt = (v: number | null, unit = ''): string => (v === null ? '-' : `${v}${unit}`);

export default function QuerySetStats({ filter }: {
  filter: { domain?: string; engine?: string; from?: string; to?: string; querySet?: string };
}): JSX.Element | null {
  const [stats, setStats] = useState<QuerySetStat[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Sets kept out of the reports. Listed separately so hiding is visibly
  // reversible rather than looking like a delete.
  const [hidden, setHidden] = useState<HiddenSet[]>([]);
  // Every set's shape over time. Loaded once for the whole table: the combined
  // chart and each row's inline chart are the same data, filtered.
  const [trends, setTrends] = useState<SetTrend[]>([]);
  const [openTrend, setOpenTrend] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  const { domain, engine, from, to, querySet } = filter;
  const load = (): void => {
    getQuerySetStats({ domain, engine, from, to })
      .then((s) => { setStats(querySet ? s.filter((x) => x.name === querySet) : s); setError(null); })
      .catch((e) => setError(e instanceof Error ? e.message : 'could not load set statistics'));
    void getHiddenSets(domain).then(setHidden).catch(() => setHidden([]));
    // Every field, not just the domain. Passing one of five meant choosing a
    // set AND an engine still drew both engines, which reads as a filter that
    // does not work rather than as one field being ignored.
    void getSetTrends({ domain, engine, from, to, name: querySet })
      .then(setTrends).catch(() => setTrends([]));
  };
  useEffect(load, [domain, engine, from, to, querySet]);

  const toggleHidden = (name: string, dom: string, hide: boolean): void => {
    setBusy(true);
    void setQuerySetHidden(name, dom, hide)
      .then(() => load())
      .catch((e) => setError(e instanceof Error ? e.message : 'could not change that'))
      .finally(() => setBusy(false));
  };

  if (error) return <p className="font-mono text-sm text-[#E0574A] mb-5">{error}</p>;
  if (!stats) return null;

  if (stats.length === 0) {
    return (
      <>
        <SectionLabel>Question sets</SectionLabel>
        <Card className="p-4 mt-3 mb-5">
          <p className="text-sm text-text-secondary">
            No named sets have been run in this range yet. Name a set when you run one. A trend is only
            readable across runs that asked the same questions.
          </p>
        </Card>
      </>
    );
  }

  return (
    <>
      <SectionLabel>Question sets</SectionLabel>
      <Card className="p-4 mt-3 mb-5">
        <div className="flex items-center gap-2 mb-1">
          <h3 className="font-serif text-lg">How each set performs</h3>
          <Info term="question_set" />
        </div>
        <p className="text-sm text-text-secondary mb-4">
          Grouped by the set name recorded on each run, so a set that was later renamed or deleted still
          shows its history. Named % is weighted by questions asked. A run of 25 does not count the same
          as a run of 5.
        </p>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-text-secondary font-mono text-xs text-left">
              <th className="py-1 pr-3">Set</th>
              <th className="pr-3">Runs</th>
              <th className="pr-3">Domains</th>
              <th className="pr-3">Engines</th>
              <th className="pr-3">Avg questions</th>
              <th className="pr-3">Named %</th>
              <th className="pr-3">Latest</th>
              <th className="pr-3">Trend</th>
              <th className="pr-3">Last run</th>
              <th></th>
            </tr></thead>
            <tbody>
              {stats.map((s) => {
                const mine = trends.filter((t) => t.name === s.name);
                const open = openTrend === s.name;
                return (
                <Fragment key={s.name}>
                <tr className="border-t border-warm-dark-2">
                  <td className="py-2 pr-3 text-text-primary max-w-[14rem] truncate" title={s.name}>{s.name}</td>
                  <td className="pr-3 font-mono text-text-secondary">{s.runs}</td>
                  <td className="pr-3 text-text-secondary max-w-[12rem] truncate" title={s.domains.join(', ')}>{s.domains.join(', ') || '-'}</td>
                  <td className="pr-3 font-mono text-cyan/80">{s.engines.map((e) => (e === 'gemini' ? 'Gemini' : 'GPT')).join(' · ')}</td>
                  <td className="pr-3 font-mono text-text-secondary">{fmt(s.avgQuestions)}</td>
                  <td className="pr-3 font-mono" style={{ color: s.namedPct === null ? undefined : s.namedPct > 0 ? '#6BE07A' : '#E0574A' }}>
                    {fmt(s.namedPct, '%')}
                  </td>
                  <td className="pr-3 font-mono text-text-secondary">{fmt(s.latestPct, '%')}</td>
                  <td className="pr-3 font-mono">
                    {s.trendPct === null || s.trendPct === 0
                      ? <span className="text-text-secondary/60" title={s.runs < 2 ? 'needs a second run before there is anything to compare' : 'no change'}>-</span>
                      : <span style={{ color: s.trendPct > 0 ? '#6BE07A' : '#E0574A' }}>{s.trendPct > 0 ? '▲' : '▼'}{Math.abs(s.trendPct)}</span>}
                  </td>
                  <td className="font-mono text-xs text-text-secondary">{new Date(s.lastAt).toLocaleDateString()}</td>
                  <td className="text-right">
                    {/* Hide, not delete. Every run keeps what it measured; only the
                        grouping stops listing this name, here and in the plugin. */}
                    <button
                      onClick={() => {
                        if (!window.confirm(
                          `Hide "${s.name}" from reports?\n\n`
                          + 'The runs keep everything they measured. The set stops appearing in this table '
                          + 'and in the AI EYE plugin report, and you can put it back at any time.'
                        )) return;
                        toggleHidden(s.name, s.domains[0] ?? domain ?? '', true);
                      }}
                      disabled={busy}
                      className="font-mono text-xs text-text-secondary hover:text-[#E0574A] disabled:opacity-40"
                      title="Keep this set out of reports. Nothing is deleted."
                    >hide</button>
                  </td>
                </tr>

                {/* The shape, under the row it belongs to. A number in a table
                    cannot say "four a week ago, one today", and that sentence is
                    the reason to run the same questions twice. */}
                <tr>
                  <td colSpan={10} className="pb-2">
                    <button
                      onClick={() => setOpenTrend(open ? null : s.name)}
                      aria-expanded={open}
                      className="font-mono text-xs text-cyan border border-cyan/40 rounded px-2 py-1 hover:bg-cyan/10 transition"
                    >{open ? '▴ hide trend' : '▾ view trend'}</button>
                    {open && (
                      <div className="mt-3 border border-warm-dark-2 rounded-lg p-3 bg-warm-dark/40">
                        {mine.length === 0 || mine[0]!.points.length === 0 ? (
                          <p className="font-mono text-sm text-text-secondary">No runs recorded for this set yet.</p>
                        ) : (
                          <>
                            <SetTrendChart sets={mine} compact />
                            <p className="font-mono text-xs uppercase tracking-[0.14em] text-text-secondary/70 mt-3 mb-1">
                              What this set asks ({mine[0]!.questions.length})
                            </p>
                            <p className="font-mono text-xs text-text-secondary/60 mb-1.5 leading-relaxed">
                              One dot per run, oldest on the left. Green named you, amber read your page without
                              naming you, red neither. A ring means GPT, plain is Gemini. Hover a dot for the date
                              and model.
                            </p>
                            <div className="max-h-56 overflow-y-auto">
                              <table className="w-full text-sm">
                                <tbody>
                                  {mine[0]!.questions.map((q) => (
                                    <tr key={q.query} className="border-t border-warm-dark-2/60">
                                      <td className="py-1 pr-3 text-text-secondary">{q.query}</td>
                                      {/* One dot per asking rather than two words
                                          per model: "Gemini not named · GPT named"
                                          read the same whether a question had been
                                          lost last week or had never once won. */}
                                      <td className="py-1 pr-3">
                                        <StateDots points={q.points} />
                                      </td>
                                      <td className="py-1 font-mono text-xs text-text-secondary/70 whitespace-nowrap">
                                        {q.wins}/{q.asked} runs named you
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                            {/* The engine with the most recent point: analysing
                                a set means analysing one engine's runs, because
                                the two disagree and averaging them would explain
                                a movement that neither of them had. */}
                            <SetAnalysisPanel
                              domain={mine[0]!.domain}
                              name={s.name}
                              engine={(mine[0]!.points[mine[0]!.points.length - 1]?.engine === 'openai' ? 'openai' : 'gemini')}
                            />

                            {!mine[0]!.saved && (
                              <p className="font-mono text-xs text-[#E8B04B] mt-2">
                                This set is not saved any more, so it cannot be run again as it is. It can be
                                restored from the panel above.
                              </p>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
                </Fragment>
              );})}
            </tbody>
          </table>
        </div>

        {/* Every set on one axis, with a toggle each. Sets are not comparable as
            a ranking, different questions, but their SHAPES over the same
            weeks are, and that is what somebody is looking for here. */}
        {trends.length > 1 && (
          <div className="mt-4 pt-4 border-t border-warm-dark-2">
            <button
              onClick={() => setShowAll((v) => !v)}
              aria-expanded={showAll}
              className="font-mono text-xs text-cyan border border-cyan/40 rounded px-2.5 py-1.5 hover:bg-cyan/10 transition"
            >{showAll ? '▴ hide all sets on one chart' : '▾ compare all sets on one chart'}</button>
            {showAll && (
              <div className="mt-3">
                <SetTrendChart sets={trends} />
                <p className="text-sm text-text-secondary mt-1 max-w-prose">
                  Each line is one set over time. They ask different questions, so a line being higher
                  than another says little; a line falling says something. Click a name to take it off.
                </p>
              </div>
            )}
          </div>
        )}

        {hidden.length > 0 && (
          <div className="mt-4 pt-4 border-t border-warm-dark-2">
            <p className="font-mono text-xs uppercase tracking-[0.14em] text-text-secondary/70 mb-2">
              Hidden from reports ({hidden.length})
            </p>
            <p className="text-sm text-text-secondary mb-2 max-w-prose">
              These are out of this table and out of the AI EYE plugin report. Nothing was deleted: every run
              still holds what it measured, and putting one back restores it everywhere.
            </p>
            <div className="flex flex-wrap gap-2">
              {hidden.map((h) => (
                <span key={`${h.domain}/${h.name}`}
                  className="font-mono text-xs border border-warm-dark-2 rounded px-2 py-1 text-text-secondary">
                  {h.name}
                  <span className="text-text-secondary/60"> · {h.domain}</span>
                  <button
                    onClick={() => toggleHidden(h.name, h.domain, false)}
                    disabled={busy}
                    className="ml-2 text-cyan hover:underline disabled:opacity-40"
                  >show again</button>
                </span>
              ))}
            </div>
          </div>
        )}

        {/*
          The chart is gone.

          A bar chart across set NAMES is not a time series and not a
          comparison of like with like: different sets ask different questions,
          so ranking them by Named % says more about which questions were picked
          than about anything that changed. The table below already carries every
          number it plotted, with the trend column doing the one comparison that
          is actually valid (a set against its own earlier runs).
        */}
      </Card>
    </>
  );
}
